/* eslint-disable */
import React, { useEffect, useState } from "react";
import Select from "react-select";
import AsyncSelect from "react-select/async";
import CreatableSelect from "react-select/creatable";
import { reactSelectStyle } from "Config/Config";
import * as url from "helper/UrlHelper";
import {
  getData,
  postData,
  putData,
  uploadMultipleFile,
  uploadSingleFile,
} from "utils/Gateway";
import { fetchOptions } from "helper/Common/CommonHelper";
import { getMemberOptions } from "helper/ContactHelper/ContactHelper";
import { useTranslation } from "react-i18next";
import FileUploadField from "components/Common/FileUploadField/FileUploadField";

// Import medical data
import { interventionTypeOptions } from "data/medical/interventionTypes";
import { roleOptions } from "data/medical/roles";
import { medicineOptions } from "data/medical/medicines";
import { labTestOptions } from "data/medical/labTests";
import { imagingOptions } from "data/medical/imaging";
import { therapyOptions } from "data/medical/therapies";
import { allergyOptions } from "data/medical/allergies";
import { pastMedicalIssuesOptions } from "data/medical/pastMedicalIssues";

// Helper function to map saved values to option objects
  const mapValuesToOptions = (savedValues, optionsList) => {
    if (!savedValues || !Array.isArray(savedValues)) return [];
    
    return savedValues.map(savedValue => {
      const foundOption = optionsList.find(option => option.value === savedValue);
      return foundOption || { value: savedValue, label: savedValue }; // fallback if not found in options
    }).filter(Boolean);
  };

// Helper function to map single value to option object
  const mapValuesToSingleOption = (savedValue, optionsList) => {
    if (!savedValue) return null;
    
    const foundOption = optionsList.find(option => option.value === savedValue);
    return foundOption || { value: savedValue, label: savedValue }; // fallback if not found in options
  };

// Step Modal component
const StepModal = ({ 
  isOpen, 
  onClose, 
  onSave, 
  editingStep, 
  phaseIndex 
}) => {
  const [id, setId] = useState(null);
  const [title, setTitle] = useState("");
  const [interventiontype, setInterventiontype] = useState(null);
  const [durationdays, setDurationdays] = useState("");
  const [instructiontopatient, setInstructiontopatient] = useState("");
  const [medicines, setMedicines] = useState([]);
  const [labTests, setLabTests] = useState([]);
  const [imaging, setImaging] = useState([]);
  const [therapies, setTherapies] = useState([]);

  useEffect(() => {
    if (editingStep) {
      setId(editingStep._id || null);
      setTitle(editingStep.title || "");
      setInterventiontype(editingStep.interventiontype || null);
      setDurationdays(editingStep.durationdays || "");
      setInstructiontopatient(editingStep.instructiontopatient || "");
      setMedicines(editingStep.medicines || []);
      setLabTests(editingStep.labTests || []);
      setImaging(editingStep.imaging || []);
      setTherapies(editingStep.therapies || []);
    } else {
      resetForm();
    }
  }, [editingStep, isOpen]);

  const resetForm = () => {
    setId(null);
    setTitle("");
    setInterventiontype(null);
    setDurationdays("");
    setInstructiontopatient("");
    setMedicines([]);
    setLabTests([]);
    setImaging([]);
    setTherapies([]);
  };

  const handleSave = () => {
    const stepData = {
      _id: id,
      title,
      interventiontype,
      durationdays: parseInt(durationdays) || 0,
      instructiontopatient,
      medicines,
      labTests,
      imaging,
      therapies
    };
    
    onSave(stepData, phaseIndex, editingStep?.stepIndex);
    resetForm();
    onClose();
  };

  // Custom styles for white background
  const whiteSelectStyle = {
    ...reactSelectStyle("3px", "6px", true, false),
    control: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      borderColor: '#dee2e6',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'white',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#007bff' : 'white',
      '&:hover': {
        backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa',
      },
    }),
  };

  if (!isOpen) return null;

  return (
    <div className="modal fade show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
      <div className="modal-dialog modal-lg">
        <div className="modal-content">
          <div className="modal-header bg-primary text-white">
            <h5 className="modal-title">
              {editingStep ? 'Edit Step' : 'Add Step'}
            </h5>
            <button type="button" className="btn-close btn-close-white" onClick={onClose}></button>
          </div>
          
          <div className="modal-body">
            {/* Step Name - Single Row */}
            <div className="row">
              <div className="col-md-12 mb-3">
                <label className="d-block fw-semibold mb-2">Step Name *</label>
                <input
                  type="text"
                  value={title}
                  onChange={(e) => setTitle(e.target.value)}
                  className="form-control fs-sm shadow-none border border-gray-300"
                  style={{ backgroundColor: 'white' }}
                  placeholder="Enter step name"
                />
              </div>
            </div>

            <div className="row">
              <div className="col-md-6 mb-3">
                <label className="d-block fw-semibold mb-2">Type of Intervention *</label>
                <Select
                  options={interventionTypeOptions}
                  value={interventiontype}
                  onChange={setInterventiontype}
                  isClearable
                  placeholder="Select intervention type"
                  styles={whiteSelectStyle}
                />
              </div>
              <div className="col-md-6 mb-3">
                <label className="d-block fw-semibold mb-2">Duration (Days) *</label>
                <input
                  type="number"
                  min="1"
                  value={durationdays}
                  onChange={(e) => setDurationdays(e.target.value)}
                  className="form-control fs-sm shadow-none border border-gray-300"
                  style={{ backgroundColor: 'white' }}
                  placeholder="Enter duration in days"
                />
              </div>
            </div>

            {/* Conditional Select Fields Based on Intervention Type */}
            {interventiontype?.value === 'medication' && (
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fw-semibold mb-2">Medicines</label>
                  <Select
                    isMulti
                    options={medicineOptions}
                    value={medicines}
                    onChange={setMedicines}
                    isClearable
                    placeholder="Select medicines"
                    styles={whiteSelectStyle}
                  />
                </div>
              </div>
            )}

            {interventiontype?.value === 'lab_test' && (
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fw-semibold mb-2">Lab Tests</label>
                  <Select
                    isMulti
                    options={labTestOptions}
                    value={labTests}
                    onChange={setLabTests}
                    isClearable
                    placeholder="Select lab tests"
                    styles={whiteSelectStyle}
                  />
                </div>
              </div>
            )}

            {interventiontype?.value === 'imaging' && (
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fw-semibold mb-2">Imaging Studies</label>
                  <Select
                    isMulti
                    options={imagingOptions}
                    value={imaging}
                    onChange={setImaging}
                    isClearable
                    placeholder="Select imaging studies"
                    styles={whiteSelectStyle}
                  />
                </div>
              </div>
            )}

            {interventiontype?.value === 'therapy' && (
              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fw-semibold mb-2">Therapies</label>
                  <Select
                    isMulti
                    options={therapyOptions}
                    value={therapies}
                    onChange={setTherapies}
                    isClearable
                    placeholder="Select therapies"
                    styles={whiteSelectStyle}
                  />
                </div>
              </div>
            )}

            {/* Show all relevant fields for consultation, follow-up, education, etc. */}
            {(interventiontype?.value === 'consultation' || 
              interventiontype?.value === 'follow_up' || 
              interventiontype?.value === 'surgery') && (
              <>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="d-block fw-semibold mb-2">Medicines (if any)</label>
                    <Select
                      isMulti
                      options={medicineOptions}
                      value={medicines}
                      onChange={setMedicines}
                      isClearable
                      placeholder="Select medicines"
                      styles={whiteSelectStyle}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="d-block fw-semibold mb-2">Lab Tests (if any)</label>
                    <Select
                      isMulti
                      options={labTestOptions}
                      value={labTests}
                      onChange={setLabTests}
                      isClearable
                      placeholder="Select lab tests"
                      styles={whiteSelectStyle}
                    />
                  </div>
                </div>
                <div className="row">
                  <div className="col-md-6 mb-3">
                    <label className="d-block fw-semibold mb-2">Imaging (if any)</label>
                    <Select
                      isMulti
                      options={imagingOptions}
                      value={imaging}
                      onChange={setImaging}
                      isClearable
                      placeholder="Select imaging studies"
                      styles={whiteSelectStyle}
                    />
                  </div>
                  <div className="col-md-6 mb-3">
                    <label className="d-block fw-semibold mb-2">Therapies (if any)</label>
                    <Select
                      isMulti
                      options={therapyOptions}
                      value={therapies}
                      onChange={setTherapies}
                      isClearable
                      placeholder="Select therapies"
                      styles={whiteSelectStyle}
                    />
                  </div>
                </div>
              </>
            )}

            <div className="row">
              <div className="col-md-12 mb-3">
                <label className="d-block fw-semibold mb-2">Instructions to Patient</label>
                <textarea
                  rows="4"
                  value={instructiontopatient}
                  onChange={(e) => setInstructiontopatient(e.target.value)}
                  className="form-control fs-sm shadow-none border border-gray-300"
                  style={{ backgroundColor: 'white' }}
                  placeholder="Enter instructions for the patient..."
                />
              </div>
            </div>
          </div>

          <div className="modal-footer">
            <button type="button" className="btn btn-secondary" onClick={onClose}>
              Cancel
            </button>
            <button 
              type="button" 
              className="btn btn-primary"
              onClick={handleSave}
              disabled={!title || !interventiontype || !durationdays}
            >
              Save Step
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Step Block component to display steps within phases
const StepBlock = ({ step, stepIndex, phaseIndex, editStep, deleteStep }) => {
  return (
    <div className="border rounded-6 p-3 mb-2 bg-light position-relative">
      <div className="position-absolute top-0 end-0 p-2">
        <button
          type="button"
          className="btn btn-sm btn-outline-primary me-1"
          onClick={() => editStep(phaseIndex, stepIndex)}
          title="Edit Step"
          style={{ width: "28px", height: "28px", padding: "0" }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>
            edit
          </span>
        </button>
        <button
          type="button"
          className="btn btn-sm btn-outline-danger"
          onClick={() => deleteStep(phaseIndex, stepIndex)}
          title="Delete Step"
          style={{ width: "28px", height: "28px", padding: "0" }}
        >
          <span className="material-symbols-outlined" style={{ fontSize: "14px" }}>
            delete
          </span>
        </button>
      </div>
      
      <div className="row">
        <div className="col-md-8">
          <h6 className="fw-semibold text-primary mb-1">{step.title}</h6>
          <p className="mb-1 fs-sm"><strong>Type:</strong> {step.interventiontype?.label}</p>
          <p className="mb-1 fs-sm"><strong>Duration:</strong> {step.durationdays} days</p>
        </div>
        <div className="col-md-4">
          {step.medicines && step.medicines.length > 0 && (
            <div className="mb-2">
              <strong className="fs-sm">Medicines:</strong>
              <ul className="fs-sm mb-0 mt-1">
                {step.medicines.map((med, idx) => (
                  <li key={idx}>{med.label}</li>
                ))}
              </ul>
            </div>
          )}
          {step.labTests && step.labTests.length > 0 && (
            <div className="mb-2">
              <strong className="fs-sm">Lab Tests:</strong>
              <ul className="fs-sm mb-0 mt-1">
                {step.labTests.map((test, idx) => (
                  <li key={idx}>{test.label}</li>
                ))}
              </ul>
            </div>
          )}
          {step.imaging && step.imaging.length > 0 && (
            <div className="mb-2">
              <strong className="fs-sm">Imaging:</strong>
              <ul className="fs-sm mb-0 mt-1">
                {step.imaging.map((img, idx) => (
                  <li key={idx}>{img.label}</li>
                ))}
              </ul>
            </div>
          )}
          {step.therapies && step.therapies.length > 0 && (
            <div className="mb-2">
              <strong className="fs-sm">Therapies:</strong>
              <ul className="fs-sm mb-0 mt-1">
                {step.therapies.map((therapy, idx) => (
                  <li key={idx}>{therapy.label}</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </div>
      
      {step.instructiontopatient && (
        <div className="mt-2">
          <p className="mb-0 fs-sm"><strong>Instructions:</strong> {step.instructiontopatient}</p>
        </div>
      )}
    </div>
  );
};

// Phase Block component
const PhaseBlock = ({
  phase,
  index,
  updatePhase,
  removePhase,
  addStepToPhase,
  editStep,
  deleteStep,
}) => {

  const accordionId = `phase-accordion-${index}`;
  const collapseId = `phase-collapse-${index}`;
  const headingId = `heading-${index}`;
  
  const [showStepModal, setShowStepModal] = useState(false);
  const [editingStep, setEditingStep] = useState(null);

  const handleAddStep = () => {
    setEditingStep(null);
    setShowStepModal(true);
  };

  const handleEditStep = (phaseIndex, stepIndex) => {
    const step = phase.steps[stepIndex];
    setEditingStep({ ...step, stepIndex });
    setShowStepModal(true);
  };

  const handleSaveStep = (stepData, phaseIndex, stepIndex) => {
    if (stepIndex !== undefined) {
      // Edit existing step
      editStep(index, stepIndex, stepData);
    } else {
      // Add new step
      addStepToPhase(index, stepData);
    }
  };

  const handleDeleteStep = (phaseIndex, stepIndex) => {
    deleteStep(index, stepIndex);
  };

  return (
    <>
      <div className="accordion mb-3" id={accordionId}>
        <div className="accordion-item border border-gray-300 rounded-8 shadow-sm">
          <h6
            className="accordion-header d-flex align-items-center"
            id={headingId}
          >
            <button
              className="accordion-button collapsed bg-light fw-semibold py-3 px-3 border-0 rounded-top-8 flex-grow-1"
              type="button"
              data-bs-toggle="collapse"
              data-bs-target={`#${collapseId}`}
              aria-expanded="false"
              aria-controls={collapseId}
            >
              <div className="d-flex align-items-center w-100">
                <span className="text-primary me-2">Phase {index + 1}</span>
                {phase.title && (
                  <span className="text-muted"> : {phase.title}</span>
                )}
                <div className="ms-auto d-flex align-items-center gap-2">
                  {phase.steps && phase.steps.length > 0 && (
                    <span className="badge bg-info d-flex align-items-center gap-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "12px" }}>
                        assignment
                      </span>
                      {phase.steps.length} steps
                    </span>
                  )}
                  {phase.days && (
                    <span className="badge bg-success d-flex align-items-center gap-1">
                      <span className="material-symbols-outlined" style={{ fontSize: "12px" }}>
                        calendar_today
                      </span>
                      {phase.days} days
                    </span>
                  )}
                </div>
              </div>
            </button>
            <button
              type="button"
              className="btn btn-sm btn-outline-danger ms-2 me-3 d-flex align-items-center"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                removePhase(index);
              }}
              title="Remove Phase"
              style={{ minWidth: "32px", height: "32px" }}
            >
              <span
                className="material-symbols-outlined"
                style={{ fontSize: "16px" }}
              >
                delete
              </span>
            </button>
          </h6>
          <div
            id={collapseId}
            className="accordion-collapse collapse"
            aria-labelledby={headingId}
            data-bs-parent={`#${accordionId}`}
          >
            <div className="accordion-body p-3 bg-white">
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="d-block fw-semibold mb-2">Phase Name *</label>
                  <input
                    type="text"
                    value={phase.title || ""}
                    onChange={(e) =>
                      updatePhase(index, "title", e.target.value)
                    }
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: 'white' }}
                    placeholder="Enter phase name"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="d-block fw-semibold mb-2">Duration (Days) *</label>
                  <input
                    type="number"
                    min="1"
                    value={phase.days || ""}
                    onChange={(e) =>
                      updatePhase(index, "days", parseInt(e.target.value) || "")
                    }
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: 'white' }}
                    placeholder="Enter duration in days"
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="d-block fw-semibold mb-2">
                    Responsible Person Role *
                  </label>
                  {/* Add debugging logs */}
                  
                  <Select
                    options={roleOptions}
                    value={phase.responsiblepersonrole}
                    onChange={(val) => updatePhase(index, "responsiblepersonrole", val)}
                    isClearable
                    placeholder="Select responsible person role"
                    styles={{
                      ...reactSelectStyle("3px", "6px", true, false),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: 'white',
                      }),
                    }}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fw-semibold mb-2">Description *</label>
                  <textarea
                    rows="3"
                    value={phase.description || ""}
                    onChange={(e) =>
                      updatePhase(index, "description", e.target.value)
                    }
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: 'white' }}
                    placeholder="Brief description of this phase..."
                  />
                </div>
              </div>

              {/* Steps Section */}
              <div className="border-top pt-3">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h6 className="fw-semibold mb-0">Steps</h6>
                  <button
                    type="button"
                    className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                    onClick={handleAddStep}
                  >
                    <span className="material-symbols-outlined">add</span>
                    Add Steps
                  </button>
                </div>

                {phase.steps && phase.steps.length > 0 ? (
                  <div>
                    {phase.steps.map((step, stepIndex) => (
                      <StepBlock
                        key={stepIndex}
                        step={step}
                        stepIndex={stepIndex}
                        phaseIndex={index}
                        editStep={handleEditStep}
                        deleteStep={handleDeleteStep}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-3 text-muted">
                    <span className="material-symbols-outlined fs-4">assignment</span>
                    <p className="mb-0 fs-sm">No steps added yet. Click "Add Steps" to start.</p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Step Modal */}
      <StepModal
        isOpen={showStepModal}
        onClose={() => setShowStepModal(false)}
        onSave={handleSaveStep}
        editingStep={editingStep}
        phaseIndex={index}
      />
    </>
  );
};

const SaveTreatmentTemplate = ({
  selectedTemplateId,
  setSelectedTemplateId,
  afterActionClose,
  setShowAlert,
  setAlertMessage,
  setMessageType,
}) => {
  const token = localStorage.getItem("token");
  const userInfo = JSON.parse(localStorage.getItem("userInfo"));
  const { t } = useTranslation();

  // Add loading state for data fetching
  const [isLoadingData, setIsLoadingData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Template Details states
  const [name, setName] = useState("");
  const [type, setType] = useState(null);
  const [company, setCompany] = useState(null);
  const [specialty, setSpecialty] = useState(null);
  const [summary, setSummary] = useState("");
  const [status, setStatus] = useState(null);

  // Meta Data states - Updated for select arrays
  const [ageRange, setAgeRange] = useState({ min: "", max: "" });
  const [weightRange, setWeightRange] = useState({ min: "", max: "" });
  const [heightRange, setHeightRange] = useState({ min: "", max: "" });
  const [totalDays, setTotalDays] = useState(1);
  const [knownAllergies, setKnownAllergies] = useState([]);
  const [knownMedication, setKnownMedication] = useState([]);
  const [knownMedicalHistory, setKnownMedicalHistory] = useState("");
  const [knownPastMedicalIssues, setKnownPastMedicalIssues] = useState([]);

  // Phases state
  const [phases, setPhases] = useState([]);

  const [isSaving, setIsSaving] = useState(false);

  // Template Type options
  const typeOptions = [
    { label: "Treatment Plan", value: "treatment_plan" },
    { label: "Diagnostic Protocol", value: "diagnostic_protocol" },
    { label: "Medication Regimen", value: "medication_regimen" },
    { label: "Rehabilitation Program", value: "rehabilitation_program" },
    { label: "Preventive Care", value: "preventive_care" },
  ];

  // Status options
  const statusOptions = [
    { label: "Active", value: "active" },
    { label: "Draft", value: "draft" },
    { label: "Inactive", value: "inactive" },
    { label: "Archived", value: "archived" },
  ];

  // Async load options for company
  const loadCompanyOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_ALL_COMPANY_LIST +
        `?token=${token}&filtername=${inputValue}`;
      const response = await getData(requestUrl);
      return response.data;
    } catch (error) {
      return [];
    }
  };

  // Async load options for specialty/category
  const loadSpecialtyOptions = async (inputValue) => {
    try {
      const requestUrl =
        url.API_BASE_URL +
        url.API_GET_CATEGORIES +
        `?token=${token}&slug=medical-specialty&search=${inputValue}`;
      const response = await getData(requestUrl);
      return response.data.map((specialty) => ({
        value: specialty._id,
        label: specialty.name,
      }));
    } catch (error) {
      return [];
    }
  };

  

  //function for get treatment template details
  const getTreatmentTemplateDetails = async () => {
    try {
      setIsLoading(true);
      
      let requestUrl =
        url.API_BASE_URL +
        url.API_GET_TREATMENT_BY_ID +
        `/${selectedTemplateId}?token=${token}`;

      console.log("Request URL for template details:", requestUrl);

      const response = await getData(requestUrl);

      console.log("Template details response:", response);

      if (response.status) {
        const templateData = response.data;

        // Map template details
        setName(templateData.title || "");

        setTotalDays(templateData.days || 1);
        
        // Map treatmenttype value to the corresponding option object
        const treatmentTypeValue = templateData.medicalextension?.treatmenttype;
        const mappedType = treatmentTypeValue 
          ? typeOptions.find(option => option.value === treatmentTypeValue) || null
          : null;
        setType(mappedType);
        
        setCompany(templateData.companyid ? { value: templateData.companyid._id, label: templateData.companyid.name } : null);
        setSpecialty(templateData.projecttypeid ? { value: templateData.projecttypeid._id, label: templateData.projecttypeid.name } : null);
        setSummary(templateData.summary || "");
        setStatus(templateData.status ? { value: templateData.status, label: templateData.status } : null);

        // Map meta data
        setAgeRange(templateData.medicalextension ? { min: templateData.medicalextension.minage || "", max: templateData.medicalextension.maxage || "" } : { min: "", max: "" });
        setWeightRange(templateData.medicalextension ? { min: templateData.medicalextension.minweight || "", max: templateData.medicalextension.maxweight || "" } : { min: "", max: "" });
        setHeightRange(templateData.medicalextension ? { min: templateData.medicalextension.minheight || "", max: templateData.medicalextension.maxheight || "" } : { min: "", max: "" });
        
        // Total days will be auto-calculated by useEffect when phases are set

        // Map multi-select values to option objects with labels
        const mappedAllergies = mapValuesToOptions(
          templateData.medicalextension?.knownallergies || [], 
          allergyOptions
        );
        setKnownAllergies(mappedAllergies);
        
        const mappedMedications = mapValuesToOptions(
          templateData.medicalextension?.knownmedications || [], 
          medicineOptions
        );
        setKnownMedication(mappedMedications);
        
        const mappedPastMedicalIssues = mapValuesToOptions(
          templateData.medicalextension?.knownpastmedicalissues || [], 
          pastMedicalIssuesOptions
        );
        setKnownPastMedicalIssues(mappedPastMedicalIssues);
        
        setKnownMedicalHistory(templateData.medicalextension?.knownmedicalhistory ?? "");

        // Map phases
        setPhases(templateData.projectsections || []);
        
        // Add debugging for phases data
        if (templateData.projectsections && templateData.projectsections.length > 0) {
          templateData.projectsections.forEach((phase, index) => {
            // set responsiblepersonrole as option object
            const mappedRole = mapValuesToSingleOption(
              phase.responsiblepersonrole, 
              roleOptions
            );
            phase.responsiblepersonrole = mappedRole;

            // Map steps data to correct field names
            if (phase.projecttasks && phase.projecttasks.length > 0) {

              console.log("phase.projecttasks :", phase.projecttasks);

              phase.steps = phase.projecttasks.map(step => ({
                _id: step._id,
                title: step.title || step.stepName, // fallback if old field name exists
                interventiontype: step.medicalextension ? 
                  mapValuesToSingleOption(step.medicalextension.interventiontype, interventionTypeOptions) : null,
                durationdays: step.medicalextension ? step.medicalextension.durationdays || 0 : 0,
                instructiontopatient: step.medicalextension?.instructiontopatient ?? '',
                // // Map medical arrays to option objects
                // medicines: mapValuesToOptions(step.medicalextension?.medicines || [], medicineOptions),
                // labTests: mapValuesToOptions(step.medicalextension?.labTests || [], labTestOptions),
                // imaging: mapValuesToOptions(step.medicalextension?.imaging || [], imagingOptions),
                // therapies: mapValuesToOptions(step.medicalextension?.therapies || [], therapyOptions),
              }));

              console.log(`Mapped steps for phase @@@ ${index + 1}:`, phase.steps);
            }

          });
        }
      }
    } catch (error) {
      console.log(error.message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedTemplateId) {
      getTreatmentTemplateDetails();
    } else {
      resetHandler();
    }
  }, [selectedTemplateId]);

  //function for save treatment template
  const saveTreatmentTemplateHandler = async () => {
    try {
      setIsSaving(true);

      const apiData = {
        id: selectedTemplateId,
        title: name,
        istemplate: true,
        type: type?.value,
        company: company?.value,
        specialty: specialty?.value,
        summary: summary,
        status: status?.value,
        ageRange: ageRange,
        weightRange: weightRange,
        heightRange: heightRange,
        days: totalDays,
        knownAllergies: knownAllergies.map(allergy => allergy.value),
        knownMedication: knownMedication.map(med => med.value),
        knownMedicalHistory: knownMedicalHistory,
        knownPastMedicalIssues: knownPastMedicalIssues.map(issue => issue.value),
        phases: phases.map(phase => ({
          id: phase._id,
          title: phase.title,
          days: phase.days,
          responsiblepersonrole: phase.responsiblepersonrole?.value,
          description: phase.description,
          steps: phase.steps ? phase.steps.map(step => ({
            id: step._id,
            title: step.title,
            interventiontype: step.interventiontype?.value,
            durationdays: step.durationdays,
            instructiontopatient: step.instructiontopatient,
            medicines: step.medicines ? step.medicines.map(med => med.value) : [],
            labTests: step.labTests ? step.labTests.map(test => test.value) : [],
            imaging: step.imaging ? step.imaging.map(img => img.value) : [],
            therapies: step.therapies ? step.therapies.map(therapy => therapy.value) : [],
            surgery: step.surgery ? step.surgery.map(surg => surg.value) : []
          })) : []
        }))
      };

      console.log("apiData", apiData);

      let requestUrl = url.API_BASE_URL;
      let response = {};

      
      requestUrl += url.API_SAVE_TREATMENT + `?token=${token}`;
      response = await postData(requestUrl, apiData);

      console.log("Response after saving template:", response);

      if(response.status){
        setSelectedTemplateId(response.data._id);
        setAlertMessage(t("template saved successfully"));
        setMessageType("success");
        setShowAlert(true); 

        // //close offcanvas
        // bootstrap.Offcanvas.getInstance(
        //   document.querySelector("#saveTreatmentTemplateOffCanvas")
        // ).hide();
        // resetHandler();
        // afterActionClose([response.data]);
      }
    

      setIsSaving(false);


    } catch (error) {
      console.log(error.message);
      setIsSaving(false);
    }
  };

  // Add new phase to the array
  const addPhase = () => {
    const newPhase = {
      phaseName: "",
      durationInDays: "",
      responsiblePersonRole: null,
      description: "",
      steps: [],
    };
    setPhases([...phases, newPhase]);
  };

  // Remove phase from array
  const removePhase = (index) => {
    setPhases(phases.filter((_, i) => i !== index));
  };

  // Update specific phase
  const updatePhase = (index, field, value) => {
    const updatedPhases = [...phases];
    updatedPhases[index][field] = value;
    setPhases(updatedPhases);
  };

  // Add step to specific phase
  const addStepToPhase = (phaseIndex, stepData) => {
    const updatedPhases = [...phases];
    if (!updatedPhases[phaseIndex].steps) {
      updatedPhases[phaseIndex].steps = [];
    }
    updatedPhases[phaseIndex].steps.push(stepData);
    setPhases(updatedPhases);
  };

  // Edit step in specific phase
  const editStep = (phaseIndex, stepIndex, stepData) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].steps[stepIndex] = stepData;
    setPhases(updatedPhases);
  };

  // Delete step from specific phase
  const deleteStep = (phaseIndex, stepIndex) => {
    const updatedPhases = [...phases];
    updatedPhases[phaseIndex].steps.splice(stepIndex, 1);
    setPhases(updatedPhases);
  };

  // Auto-calculate total days from phases
  useEffect(() => {
    const calculatedDays = phases.reduce((total, phase) => {
      const phaseDays = parseInt(phase.days) || 0;
      return total + phaseDays;
    }, 0);
    
    // Set minimum of 1 day
    setTotalDays(calculatedDays > 0 ? calculatedDays : 1);
  }, [phases]);

  const resetHandler = () => {
    // Reset template details
    setName("");
    setType(null);
    setCompany(null);
    setSpecialty(null);
    setSummary("");
    setStatus(null);

    // Reset meta data
    setAgeRange({ min: "", max: "" });
    setWeightRange({ min: "", max: "" });
    setHeightRange({ min: "", max: "" });
    setTotalDays(1);
    setKnownAllergies([]);

    // Reset phases
    setPhases([]);

    setSelectedTemplateId(null);
  };

  // Custom styles for white background creatable selects
  const whiteCreatableSelectStyle = {
    ...reactSelectStyle("3px", "6px", true, false),
    control: (provided) => ({
      ...provided,
      backgroundColor: 'white',
      borderColor: '#dee2e6',
    }),
    menu: (provided) => ({
      ...provided,
      backgroundColor: 'white',
    }),
    option: (provided, state) => ({
      ...provided,
      backgroundColor: state.isSelected ? '#007bff' : 'white',
      '&:hover': {
        backgroundColor: state.isSelected ? '#007bff' : '#f8f9fa',
      },
    }),
  };

  if (isLoadingData || isLoading) {
    return (
      <div
        className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
        tabIndex="-1"
        id="saveTreatmentTemplateOffCanvas"
        aria-labelledby="saveTreatmentTemplateOffCanvas"
      >
        {/* Header */}
        <div className="offcanvas-header bg-primary text-white p-4 pb-3 border-bottom border-gray-300">
          <div className="d-flex align-items-center gap-3">
            <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
              <span className="material-symbols-outlined text-primary fs-5 icon-md">
                medical_services
              </span>
            </div>
            <div>
              <h3 className="offcanvas-title fw-bold text-white mb-0">
                Save Treatment Template
              </h3>
              <p className="text-white fs-sm mb-0">
                Add or update treatment template details
              </p>
            </div>
          </div>
          <button
            type="button"
            className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
            data-bs-dismiss="offcanvas"
            aria-label="Close"
            onClick={resetHandler}
          ></button>
        </div>
        
        <div className="offcanvas-body p-4">
          <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm">
            {/* Loading skeleton placeholder */}
            <div className="placeholder-glow d-flex flex-column gap-4">
              <span className="placeholder placeholder-lg bg-secondary col-12"></span>
              <span className="placeholder placeholder-lg bg-secondary col-8"></span>
              <span className="placeholder placeholder-lg bg-secondary col-4"></span>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className="offcanvas lg offcanvas-end bg-white border-0 shadow-lg"
      tabIndex="-1"
      id="saveTreatmentTemplateOffCanvas"
      aria-labelledby="saveTreatmentTemplateOffCanvas"
    >
      {/* Header */}
      <div className="offcanvas-header bg-primary text-white p-4 pb-3 border-bottom border-gray-300">
        <div className="d-flex align-items-center gap-3">
          <div className="icon-wrapper p-3 bg-white rounded-circle d-flex align-items-center justify-content-center">
            <span className="material-symbols-outlined text-primary fs-5 icon-md">
              medical_services
            </span>
          </div>
          <div>
            <h3 className="offcanvas-title fw-bold text-white mb-0">
              Save Treatment Template
            </h3>
            <p className="text-white fs-sm mb-0">
              Add or update treatment template details
            </p>
          </div>
        </div>
        <button
          type="button"
          className="btn-close p-2 bg-gray-200 rounded-circle shadow-sm border-0"
          data-bs-dismiss="offcanvas"
          aria-label="Close"
          onClick={resetHandler}
        ></button>
      </div>
      
      <div className="offcanvas-body p-4">
        <div className="p-3 p-md-4 border border-gray-300 rounded-10 shadow-sm">
          <form onSubmit={(e) => e.preventDefault()}>
            {/* Template Details Section */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <h5 className="mb-3 fw-semibold">{t("Template Details")}</h5>
              
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Template Name <span className="text-danger">*</span>
                  </label>
                  <input
                    type="text"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: 'white' }}
                    placeholder="Enter template name"
                  />
                </div>
                <div className="col-md-6 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Type <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={typeOptions}
                    value={type}
                    onChange={setType}
                    isClearable
                    placeholder="Select template type"
                    styles={{
                      ...reactSelectStyle("3px", "6px", true, false),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: 'white',
                      }),
                    }}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Company
                  </label>
                  <AsyncSelect
                    loadOptions={loadCompanyOptions}
                    defaultOptions={false}
                    cacheOptions={false}
                    value={company}
                    onChange={setCompany}
                    isClearable
                    placeholder="Search or select company..."
                    styles={{
                      ...reactSelectStyle("3px", "6px", true, false),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: 'white',
                      }),
                    }}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Specialty <span className="text-danger">*</span>
                  </label>
                  <AsyncSelect
                    loadOptions={loadSpecialtyOptions}
                    defaultOptions={true}
                    cacheOptions={false}
                    value={specialty}
                    onChange={setSpecialty}
                    isClearable
                    placeholder="Search or select specialty..."
                    styles={{
                      ...reactSelectStyle("3px", "6px", true, false),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: 'white',
                      }),
                    }}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Status <span className="text-danger">*</span>
                  </label>
                  <Select
                    options={statusOptions}
                    value={status}
                    onChange={setStatus}
                    isClearable
                    placeholder="Select status"
                    styles={{
                      ...reactSelectStyle("3px", "6px", true, false),
                      control: (provided) => ({
                        ...provided,
                        backgroundColor: 'white',
                      }),
                    }}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Summary
                  </label>
                  <textarea
                    rows="3"
                    value={summary}
                    onChange={(e) => setSummary(e.target.value)}
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: 'white' }}
                    placeholder="Enter template summary"
                  />
                </div>
              </div>
            </div>

            {/* Meta Data Section */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <h5 className="mb-3 fw-semibold">{t("Meta Data")}</h5>
              
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Age Range
                  </label>
                  <div className="d-flex gap-2">
                    <input
                      type="number"
                      value={ageRange.min}
                      onChange={(e) => setAgeRange({...ageRange, min: e.target.value})}
                      className="form-control fs-sm shadow-none border border-gray-300"
                      style={{ backgroundColor: 'white' }}
                      placeholder="Min"
                    />
                    <input
                      type="number"
                      value={ageRange.max}
                      onChange={(e) => setAgeRange({...ageRange, max: e.target.value})}
                      className="form-control fs-sm shadow-none border border-gray-300"
                      style={{ backgroundColor: 'white' }}
                      placeholder="Max"
                    />
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Weight Range (kg)
                  </label>
                  <div className="d-flex gap-2">
                    <input
                      type="number"
                      value={weightRange.min}
                      onChange={(e) => setWeightRange({...weightRange, min: e.target.value})}
                      className="form-control fs-sm shadow-none border border-gray-300"
                      style={{ backgroundColor: 'white' }}
                      placeholder="Min"
                    />
                    <input
                      type="number"
                      value={weightRange.max}
                      onChange={(e) => setWeightRange({...weightRange, max: e.target.value})}
                      className="form-control fs-sm shadow-none border border-gray-300"
                      style={{ backgroundColor: 'white' }}
                      placeholder="Max"
                    />
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Height Range (cm)
                  </label>
                  <div className="d-flex gap-2">
                    <input
                      type="number"
                      value={heightRange.min}
                      onChange={(e) => setHeightRange({...heightRange, min: e.target.value})}
                      className="form-control fs-sm shadow-none border border-gray-300"
                      style={{ backgroundColor: 'white' }}
                      placeholder="Min"
                    />
                    <input
                      type="number"
                      value={heightRange.max}
                      onChange={(e) => setHeightRange({...heightRange, max: e.target.value})}
                      className="form-control fs-sm shadow-none border border-gray-300"
                      style={{ backgroundColor: 'white' }}
                      placeholder="Max"
                    />
                  </div>
                </div>
                <div className="col-md-3 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Days
                  </label>
                  <input
                    type="number"
                    value={totalDays}
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: '#f8f9fa' }}
                    placeholder="Auto-calculated"
                    readOnly
                    title="Auto-calculated from phase durations"
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Known Allergies
                  </label>
                  <CreatableSelect
                    isMulti
                    options={allergyOptions}
                    value={knownAllergies}
                    onChange={setKnownAllergies}
                    isClearable
                    placeholder="Select or create allergies..."
                    styles={whiteCreatableSelectStyle}
                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Known Medication
                  </label>
                  <CreatableSelect
                    isMulti
                    options={medicineOptions}
                    value={knownMedication}
                    onChange={setKnownMedication}
                    isClearable
                    placeholder="Select or create medications..."
                    styles={whiteCreatableSelectStyle}
                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  />
                </div>
                <div className="col-md-4 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Known Past Medical Issues
                  </label>
                  <CreatableSelect
                    isMulti
                    options={pastMedicalIssuesOptions}
                    value={knownPastMedicalIssues}
                    onChange={setKnownPastMedicalIssues}
                    isClearable
                    placeholder="Select or create past medical issues..."
                    styles={whiteCreatableSelectStyle}
                    formatCreateLabel={(inputValue) => `Add "${inputValue}"`}
                  />
                </div>
              </div>

              <div className="row">
                <div className="col-md-12 mb-3">
                  <label className="d-block fs-sm fw-semibold mb-2 text-dark">
                    Known Medical History
                  </label>
                  <textarea
                    rows="3"
                    value={knownMedicalHistory}
                    onChange={(e) => setKnownMedicalHistory(e.target.value)}
                    className="form-control fs-sm shadow-none border border-gray-300"
                    style={{ backgroundColor: 'white' }}
                    placeholder="Enter detailed medical history..."
                  />
                </div>
              </div>
            </div>

            {/* Treatment Phases Section */}
            <div className="form-group mb-4 p-3 border rounded-8 shadow-sm bg-white">
              <div className="d-flex justify-content-between align-items-center mb-3">
                <h5 className="mb-0 fw-semibold">{t("Treatment Phases")}</h5>
                <button
                  type="button"
                  className="btn btn-primary btn-sm d-flex align-items-center gap-1"
                  onClick={addPhase}
                >
                  <span className="material-symbols-outlined me-1">add</span>
                  Add Phase
                </button>
              </div>

              {phases.length === 0 ? (
                <div className="text-center py-4 text-muted">
                  <span className="material-symbols-outlined fs-1">
                    timeline
                  </span>
                  <p className="mb-0">
                    No phases added yet. Click "Add Phase" to start.
                  </p>
                </div>
              ) : (
                <>
                  {phases.map((phase, index) => (
                    <PhaseBlock
                      key={index}
                      phase={phase}
                      index={index}
                      updatePhase={updatePhase}
                      removePhase={removePhase}
                      addStepToPhase={addStepToPhase}
                      editStep={editStep}
                      deleteStep={deleteStep}
                    />
                  ))}

                  {/* Add Phase button at the bottom */}
                  <div className="d-flex justify-content-center mt-3">
                    <button
                      type="button"
                      className="btn btn-outline-primary btn-sm d-flex align-items-center gap-1"
                      onClick={addPhase}
                    >
                      <span className="material-symbols-outlined">add</span>
                      Add Another Phase
                    </button>
                  </div>
                </>
              )}
            </div>

            <div className="group_btnOuter d-flex justify-content-end pt-3 border-top mt-auto">
              <button
                type="button"
                className="btn btn-primary d-flex align-items-center gap-2"
                onClick={saveTreatmentTemplateHandler}
                disabled={isSaving}
                style={{
                  cursor: isSaving ? "not-allowed" : "pointer",
                }}
              >
                {isSaving ? (
                  <>
                    <span
                      className="spinner-border spinner-border-sm"
                      role="status"
                      aria-hidden="true"
                    ></span>
                    Saving...
                  </>
                ) : (
                  <>
                    <span className="material-symbols-outlined me-1">
                      save
                    </span>
                    Save Template
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default SaveTreatmentTemplate;
